import React, { useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { MoreVertical, X, FolderOpen } from 'lucide-react'
import AttachmentGalleryModal, { GalleryFile } from '../AttachmentGalleryModal'
import AttachmentSourceModal from '../AttachmentSourceModal'
import { openSignedAttachment, getSignedAttachmentUrl } from '../../utils/openSignedAttachment'
import cross from '../../assets/produtcss/cross.svg'
import eye from '../../assets/fashion/OrderForm/eye.svg'
import crossIcon from '../../assets/fashion/OrderForm/cross.svg'
import downloadIcon from '../../assets/fashion/OrderForm/download.svg'
import {
  FileUploadContainer,
  FileUploadLabel,
  FileNameText,
  ExistingAttachmentCard,
  ExistingAttachmentCoverTag,
  ExistingAttachmentDropdownPortal,
  ExistingAttachmentDropdownItem,
  ExistingAttachmentFileBadge,
  ExistingAttachmentGrid,
  ExistingAttachmentMenuButton,
  ExistingAttachmentName,
  ExistingAttachmentPreview,
  AttachmentListContainer,
  AttachmentListRow,
  AttachmentListName,
  AttachmentListActions,
  AttachmentListLinkButton,
  AttachmentListIconButton,
} from './style'

export interface ExistingAttachment {
  id: number
  originalName: string
  fileName: string
  filePath: string
  fileSize: number
  mimeType: string
  fileUrl?: string
  isImage?: boolean
  isCover?: boolean
  coverScale?: number | null
  coverPosX?: number | null
  coverPosY?: number | null
}

interface AttachmentManagerProps {
  selectedFiles: File[]
  existingAttachments?: ExistingAttachment[]
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveFile: (index: number) => void
  onViewAttachment?: (attachment: ExistingAttachment) => void
  onDownloadAttachment?: (attachment: ExistingAttachment) => void
  onRemoveExistingAttachment?: (attachmentId: number) => void
  onSetCoverAttachment?: (attachment: ExistingAttachment) => void
  onRemoveCoverAttachment?: (attachment: ExistingAttachment) => void
  formatFileSize: (bytes: number) => string
  maxFileSize?: number
  accept?: string
  multiple?: boolean
  onPreviewFile?: (file: File) => void
  onDownloadFile?: (file: File) => void
  readOnly?: boolean
  existingAttachmentViewMode?: 'list' | 'ticket'
  inlineMode?: boolean
  onSelectFromGallery?: (attachments: ExistingAttachment[]) => void
}

const AttachmentManager: React.FC<AttachmentManagerProps> = ({
  selectedFiles,
  existingAttachments = [],
  onFileChange,
  onRemoveFile,
  onViewAttachment,
  onDownloadAttachment,
  onRemoveExistingAttachment,
  onSetCoverAttachment,
  onRemoveCoverAttachment,
  formatFileSize,
  accept = '*/*',
  multiple = true,
  onPreviewFile,
  onDownloadFile,
  readOnly = false,
  existingAttachmentViewMode = 'list',
  inlineMode = false,
  onSelectFromGallery,
}) => {
  const [showOverlay, setShowOverlay] = useState(false)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [isSourceChoiceOpen, setIsSourceChoiceOpen] = useState(false)
  const [activeAttachmentMenuId, setActiveAttachmentMenuId] = useState<number | null>(null)
  const [attachmentMenuPosition, setAttachmentMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const attachmentMenuButtonRefs = React.useRef<Record<number, HTMLButtonElement | null>>({})
  const attachmentDropdownRef = React.useRef<HTMLDivElement | null>(null)

  const fileUploadInputId = useMemo(
    () => `attachment-upload-${Math.random().toString(36).slice(2, 10)}`,
    [],
  )

  useEffect(() => {
    if (!showOverlay) {
      setActiveAttachmentMenuId(null)
    }
  }, [showOverlay])

  useEffect(() => {
    const handleClickOutsideMenu = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const clickedInsideDropdown =
        attachmentDropdownRef.current?.contains(target) ?? false
      const clickedInsideCardScope = Boolean(
        target.closest('[data-attachment-menu-scope="true"]'),
      )
      const clickedOnMenuButton = Object.values(attachmentMenuButtonRefs.current).some(
        (button) => button && button.contains(target),
      )

      if (!clickedInsideDropdown && !clickedInsideCardScope && !clickedOnMenuButton) {
        setActiveAttachmentMenuId(null)
      }
    }

    const handleScrollOrResize = () => {
      setActiveAttachmentMenuId(null)
    }

    document.addEventListener('mousedown', handleClickOutsideMenu)
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideMenu)
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [])

  const handleToggleAttachmentMenu = (attachmentId: number, button: HTMLButtonElement | null) => {
    if (activeAttachmentMenuId === attachmentId) {
      setActiveAttachmentMenuId(null)
      return
    }

    if (button) {
      const rect = button.getBoundingClientRect()
      const menuWidth = 220
      const menuHeight = 220
      let left = rect.left
      let top = rect.bottom + 5

      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 10
      }

      if (left < 10) {
        left = 10
      }

      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 5
      }

      if (top < 10) {
        top = 10
      }

      setAttachmentMenuPosition({ top, left })
    }

    setActiveAttachmentMenuId(attachmentId)
  }

  const isImageAttachment = (attachment: ExistingAttachment) => {
    if (attachment.isImage === true) {
      return true
    }

    const mimeType = String(attachment.mimeType || '').toLowerCase()
    if (mimeType.startsWith('image/')) {
      return true
    }

    const fileReference = String(
      attachment.originalName || attachment.fileName || attachment.fileUrl || attachment.filePath || '',
    ).toLowerCase()

    return /\.(png|jpe?g|gif|bmp|webp|svg|avif|heic|heif)$/i.test(fileReference)
  }

  const getAttachmentSource = (attachment: ExistingAttachment) => {
    return attachment.filePath || attachment.fileUrl || ''
  }

  const getFileExtensionBadge = (attachment: ExistingAttachment) => {
    const source = attachment.originalName || attachment.fileName || attachment.fileUrl || ''
    const extension = source.split('.').pop()
    if (!extension) return 'ARQ'
    return extension.slice(0, 3).toUpperCase()
  }

  const handleViewExistingAttachment = async (attachment: ExistingAttachment) => {
    if (onViewAttachment) {
      onViewAttachment(attachment)
      return
    }

    const source = getAttachmentSource(attachment)
    if (source) {
      await openSignedAttachment(source)
    }
  }

  const handleDownloadExistingAttachment = async (attachment: ExistingAttachment) => {
    if (onDownloadAttachment) {
      onDownloadAttachment(attachment)
      return
    }

    const source = getAttachmentSource(attachment)
    if (!source) {
      return
    }

    const fileName = attachment.originalName || attachment.fileName || 'anexo'
    const signedUrl = await getSignedAttachmentUrl(source, true, fileName)
    const link = document.createElement('a')
    link.href = signedUrl
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const renderCompactAttachmentList = (options?: { maxHeight?: number }) => {
    const hasPending = !readOnly && selectedFiles.length > 0
    const hasExisting = existingAttachments.length > 0

    if (!hasPending && !hasExisting) {
      return null
    }

    return (
      <AttachmentListContainer
        style={options?.maxHeight ? { maxHeight: options.maxHeight, overflowY: 'auto' } : undefined}
      >
        {hasPending &&
          selectedFiles.map((file, index) => (
            <AttachmentListRow key={`pending-${file.name}-${index}`}>
              <AttachmentListName>{file.name}</AttachmentListName>
              <AttachmentListActions>
                {onDownloadFile && (
                  <AttachmentListLinkButton type='button' onClick={() => onDownloadFile(file)}>
                    Download
                  </AttachmentListLinkButton>
                )}
                <AttachmentListIconButton type='button' onClick={() => onRemoveFile(index)}>
                  <X size={14} />
                </AttachmentListIconButton>
              </AttachmentListActions>
            </AttachmentListRow>
          ))}

        {existingAttachments.map((attachment) => (
          <AttachmentListRow key={attachment.id}>
            <AttachmentListName title={attachment.originalName || attachment.fileName}>
              {attachment.originalName || attachment.fileName}
              {attachment.isCover ? ' (Capa)' : ''}
            </AttachmentListName>
            <AttachmentListActions>
              {(onDownloadAttachment || getAttachmentSource(attachment)) && (
                <AttachmentListLinkButton
                  type='button'
                  onClick={() => handleDownloadExistingAttachment(attachment)}
                >
                  Download
                </AttachmentListLinkButton>
              )}
              {!readOnly && onRemoveExistingAttachment && (
                <AttachmentListIconButton
                  type='button'
                  onClick={() => onRemoveExistingAttachment(attachment.id)}
                >
                  <X size={14} />
                </AttachmentListIconButton>
              )}
            </AttachmentListActions>
          </AttachmentListRow>
        ))}
      </AttachmentListContainer>
    )
  }

  const renderTicketStyleExistingAttachments = () => {
    if (!existingAttachments.length) {
      return null
    }

    return (
      <ExistingAttachmentGrid>
        {existingAttachments.map((attachment) => {
          const isImage = isImageAttachment(attachment)
          const source = getAttachmentSource(attachment)
          const isMenuOpen = activeAttachmentMenuId === attachment.id
          const canShowDownload = Boolean(onDownloadAttachment || source)

          return (
            <ExistingAttachmentCard
              key={attachment.id}
              data-attachment-menu-scope='true'
              style={isMenuOpen ? { zIndex: 10004 } : undefined}
            >
              <ExistingAttachmentPreview
                onClick={() => handleViewExistingAttachment(attachment)}
              >
                {isImage && source ? (
                  <img src={source} alt={attachment.originalName || attachment.fileName} />
                ) : (
                  <ExistingAttachmentFileBadge>
                    {getFileExtensionBadge(attachment)}
                  </ExistingAttachmentFileBadge>
                )}

                {attachment.isCover && <ExistingAttachmentCoverTag>Capa</ExistingAttachmentCoverTag>}

                <ExistingAttachmentMenuButton
                  ref={(element) => {
                    attachmentMenuButtonRefs.current[attachment.id] = element
                  }}
                  type='button'
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    handleToggleAttachmentMenu(attachment.id, attachmentMenuButtonRefs.current[attachment.id])
                  }}
                >
                  <MoreVertical size={14} />
                </ExistingAttachmentMenuButton>
              </ExistingAttachmentPreview>

              <ExistingAttachmentName title={attachment.originalName || attachment.fileName}>
                {attachment.originalName || attachment.fileName}
              </ExistingAttachmentName>
            </ExistingAttachmentCard>
          )
        })}
      </ExistingAttachmentGrid>
    )
  }

  const statusText =
    selectedFiles.length > 0
      ? `${selectedFiles.length} novo(s) arquivo(s) selecionado(s)`
      : existingAttachments.length > 0
        ? `${existingAttachments.length} anexo(s) existente(s)`
        : readOnly
          ? 'Nenhum anexo'
          : 'Nenhum arquivo escolhido'

  return (
    <>
      <div style={{ position: 'relative' }}>
        {(!readOnly || !inlineMode) && (
          <FileUploadContainer
            style={inlineMode ? { paddingRight: '15px' } : undefined}
          >
            <FileUploadLabel
              htmlFor={undefined}
              onClick={
                !readOnly
                  ? (e: React.MouseEvent) => {
                      e.preventDefault()
                      if (inlineMode) {
                        setIsSourceChoiceOpen(true)
                      } else {
                        setShowOverlay(true)
                      }
                    }
                  : undefined
              }
            >
              {readOnly ? 'Ver anexos' : 'Escolher arquivos'}
            </FileUploadLabel>
            <FileNameText>{statusText}</FileNameText>
            {!inlineMode && (
              <span
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  border: '1px solid #666',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  fontStyle: 'italic',
                  cursor: 'pointer',
                  color: '#666',
                }}
                onClick={() => setShowOverlay(true)}
                title='Gerenciar anexos'
              >
                i
              </span>
            )}
          </FileUploadContainer>
        )}

        {inlineMode && !readOnly && (
          <input
            id={fileUploadInputId}
            type='file'
            style={{ display: 'none' }}
            onChange={onFileChange}
            accept={accept}
            multiple={multiple}
          />
        )}

        {existingAttachmentViewMode === 'list' &&
          renderCompactAttachmentList()}

        {!inlineMode && showOverlay && (
          <>
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9998,
              }}
              onClick={() => setShowOverlay(false)}
            />
            <div
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                padding: '24px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto',
                zIndex: 9999,
              }}
            >
              <div
                style={{
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
                  {readOnly ? 'Anexos' : 'Gerenciar Anexos'}
                </h3>
                <img
                  src={cross}
                  alt='Fechar'
                  onClick={() => setShowOverlay(false)}
                  style={{
                    cursor: 'pointer',
                    width: '16px',
                    height: '16px',
                  }}
                />
              </div>

              {!readOnly && (
                <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <FileUploadContainer>
                    <FileUploadLabel
                      htmlFor={undefined}
                      onClick={(e) => {
                        e.preventDefault()
                        setIsSourceChoiceOpen(true)
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      Escolher arquivos
                    </FileUploadLabel>
                    <input
                      id={fileUploadInputId}
                      type='file'
                      style={{ display: 'none' }}
                      onChange={onFileChange}
                      accept={accept}
                      multiple={multiple}
                    />
                    <FileNameText>
                      {selectedFiles.length > 0
                        ? `${selectedFiles.length} novo(s) arquivo(s) selecionado(s)`
                        : 'Nenhum arquivo escolhido'}
                    </FileNameText>
                  </FileUploadContainer>

                  <button
                    type='button'
                    onClick={() => setIsGalleryOpen(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: '#eff6ff',
                      color: '#0090d3',
                      border: '1px solid #bfdbfe',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 600,
                      alignSelf: 'flex-start',
                    }}
                  >
                    <FolderOpen size={14} />
                    Escolher da galeria da empresa
                  </button>
                </div>
              )}

              {existingAttachmentViewMode === 'list' &&
                (selectedFiles.length > 0 || existingAttachments.length > 0) &&
                renderCompactAttachmentList({ maxHeight: 280 })}

              {existingAttachmentViewMode === 'ticket' && !readOnly && selectedFiles.length > 0 && (
                <div
                  style={{
                    marginBottom: '20px',
                    padding: '12px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px',
                  }}
                >
                  <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                    Novos arquivos:
                  </div>
                  <div
                    style={{
                      maxHeight: '150px',
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px',
                          backgroundColor: '#fff',
                          borderRadius: '4px',
                          fontSize: '13px',
                        }}
                      >
                        <span style={{ flex: 1, wordBreak: 'break-word', marginRight: '8px' }}>
                          {file.name}
                        </span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                          {onPreviewFile && (file.type.startsWith('image/') || file.type === 'application/pdf') && (
                            <img
                              src={eye}
                              alt='Visualizar'
                              title='Visualizar'
                              onClick={() => onPreviewFile(file)}
                              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                            />
                          )}
                          {onDownloadFile && (
                            <img
                              src={downloadIcon}
                              alt='Download'
                              title='Download'
                              onClick={() => onDownloadFile(file)}
                              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                            />
                          )}
                          <img
                            src={crossIcon}
                            alt='Remover'
                            title='Remover'
                            onClick={() => onRemoveFile(index)}
                            style={{ cursor: 'pointer', width: '14px', height: '14px', flexShrink: 0 }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {existingAttachmentViewMode === 'ticket' && existingAttachments.length > 0 && (
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '4px',
                  }}
                >
                  <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                    Anexos existentes:
                  </div>
                  <div
                    style={{
                      maxHeight: '220px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      padding: '2px',
                    }}
                  >
                    {renderTicketStyleExistingAttachments()}
                  </div>
                </div>
              )}

              {readOnly && existingAttachments.length === 0 && (
                <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>Nenhum anexo</p>
              )}
            </div>

            {existingAttachmentViewMode === 'ticket' &&
              activeAttachmentMenuId !== null &&
              (() => {
                const attachment = existingAttachments.find((item) => item.id === activeAttachmentMenuId)
                if (!attachment) {
                  return null
                }

                const isImage = isImageAttachment(attachment)
                const source = getAttachmentSource(attachment)
                const canShowDownload = Boolean(onDownloadAttachment || source)

                return ReactDOM.createPortal(
                  <ExistingAttachmentDropdownPortal
                    ref={attachmentDropdownRef}
                    style={{ top: attachmentMenuPosition.top, left: attachmentMenuPosition.left }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <ExistingAttachmentDropdownItem
                      type='button'
                      onClick={() => {
                        handleViewExistingAttachment(attachment)
                        setActiveAttachmentMenuId(null)
                      }}
                    >
                      Visualizar
                    </ExistingAttachmentDropdownItem>

                    {canShowDownload && (
                      <ExistingAttachmentDropdownItem
                        type='button'
                        onClick={() => {
                          handleDownloadExistingAttachment(attachment)
                          setActiveAttachmentMenuId(null)
                        }}
                      >
                        Download
                      </ExistingAttachmentDropdownItem>
                    )}

                    {isImage && !readOnly && attachment.isCover && onRemoveCoverAttachment && (
                      <ExistingAttachmentDropdownItem
                        type='button'
                        onClick={() => {
                          onRemoveCoverAttachment(attachment)
                          setActiveAttachmentMenuId(null)
                        }}
                      >
                        Remover da capa do produto
                      </ExistingAttachmentDropdownItem>
                    )}

                    {isImage && !readOnly && !attachment.isCover && onSetCoverAttachment && (
                      <ExistingAttachmentDropdownItem
                        type='button'
                        onClick={() => {
                          onSetCoverAttachment(attachment)
                          setActiveAttachmentMenuId(null)
                        }}
                      >
                        Definir como capa do produto
                      </ExistingAttachmentDropdownItem>
                    )}

                    {!readOnly && onRemoveExistingAttachment && (
                      <ExistingAttachmentDropdownItem
                        type='button'
                        className='danger'
                        onClick={() => {
                          onRemoveExistingAttachment(attachment.id)
                          setActiveAttachmentMenuId(null)
                        }}
                      >
                        Excluir
                      </ExistingAttachmentDropdownItem>
                    )}
                  </ExistingAttachmentDropdownPortal>,
                  document.body,
                )
              })()}
          </>
        )}
      </div>
      <AttachmentSourceModal
        isOpen={isSourceChoiceOpen}
        onClose={() => setIsSourceChoiceOpen(false)}
        title='Adicionar Anexos'
        description='Escolha como deseja adicionar os arquivos:'
        onChooseUpload={() => {
          document.getElementById(fileUploadInputId)?.click()
        }}
        onChooseGallery={() => {
          setIsGalleryOpen(true)
        }}
      />

      <AttachmentGalleryModal
        isOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
        onSelect={async (galleryFiles) => {
          const converted: ExistingAttachment[] = galleryFiles.map((f) => ({
            id: f.id,
            originalName: f.originalName || f.title,
            fileName: f.fileName,
            filePath: f.signedUrl || f.fileUrl,
            fileUrl: f.fileUrl,
            fileSize: f.fileSize,
            mimeType: f.mimeType,
            isImage: f.mimeType?.startsWith('image/'),
          }))
          if (onSelectFromGallery) {
            onSelectFromGallery(converted)
          } else {
            try {
              const fetchedFiles = await Promise.all(
                galleryFiles.map(async (gf) => {
                  const signedUrl = gf.signedUrl || (await getSignedAttachmentUrl(gf.fileUrl))
                  const res = await fetch(signedUrl)
                  const blob = await res.blob()
                  return new File([blob], gf.originalName || gf.fileName || gf.title || 'anexo', {
                    type: gf.mimeType || blob.type,
                  })
                }),
              )
              const dataTransfer = new DataTransfer()
              fetchedFiles.forEach((file) => dataTransfer.items.add(file))
              const synthEvent = {
                target: {
                  files: dataTransfer.files,
                  value: '',
                },
              } as unknown as React.ChangeEvent<HTMLInputElement>
              onFileChange(synthEvent)
            } catch (err) {
              console.error(err)
            }
          }
        }}
        multiple={multiple}
        defaultFilter="all"
        title="Galeria da Empresa - Anexos"
      />
    </>
  )
}

export default AttachmentManager